import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
	Building2,
	ChevronDown,
	ChevronRight,
	ClipboardCheck,
	Edit,
	FileText,
	Loader2,
	MoreHorizontal,
	Pencil,
	Plus,
	Save,
	Scale,
	Target,
	Trash2,
	Upload,
	Users,
} from "lucide-react";
import { useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { useTRPC } from "@/integrations/trpc/react";
import { trpcClient } from "@/integrations/tanstack-query/root-provider";
import { supabase } from "@/lib/supabase-browser";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { PdfFormEditorV2 } from "@/components/pdf-editor";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { useAuth } from "@/lib/auth";

// Local organization type
interface Organization {
	id: string;
	name: string;
	type: "buyer" | "member" | "subtier";
	parentId?: string | null;
	memberCount: number;
	activeSolicitations: number;
}

interface OrgForm {
	id: string;
	name: string;
	description: string | null;
	formTemplateId: string;
	pdfStoragePath: string | null;
	required: boolean;
}


export const Route = createFileRoute("/organizations/")({
	component: OrganizationsPage,
});

function OrganizationsPage() {
	const { hasPermission, user } = useAuth();
	const trpc = useTRPC();
	const queryClient = useQueryClient();

	// Query organizations from tRPC
	const { data: orgsData, isLoading } = useQuery(
		trpc.organizations.getAccessibleOrgs.queryOptions(),
	);

	// Add organization state
	const [newOrgType, setNewOrgType] = useState<"member" | "subtier">("member");
	const [newOrgParent, setNewOrgParent] = useState<string>("");
	const [newOrgName, setNewOrgName] = useState("");
	const [newOrgEmail, setNewOrgEmail] = useState("");
	const [newOrgPhone, setNewOrgPhone] = useState("");

	// Create member mutation
	const createMemberMutation = useMutation(
		trpc.organizations.createMember.mutationOptions({
			onSuccess: () => {
				toast.success("Organization created successfully!");
				setDialogOpen(false);
				resetNewOrgForm();
				queryClient.invalidateQueries({ queryKey: trpc.organizations.getAccessibleOrgs.queryKey() });
			},
			onError: (error) => {
				toast.error(`Failed to create organization: ${error.message}`);
			},
		}),
	);

	// Create subtier mutation
	const createSubtierMutation = useMutation(
		trpc.organizations.createSubtier.mutationOptions({
			onSuccess: () => {
				toast.success("Subtier created successfully!");
				setDialogOpen(false);
				resetNewOrgForm();
				queryClient.invalidateQueries({ queryKey: trpc.organizations.getAccessibleOrgs.queryKey() });
			},
			onError: (error) => {
				toast.error(`Failed to create subtier: ${error.message}`);
			},
		}),
	);

	const resetNewOrgForm = () => {
		setNewOrgType("member");
		setNewOrgParent("");
		setNewOrgName("");
		setNewOrgEmail("");
		setNewOrgPhone("");
	};

	// Map to display format
	const organizations: Organization[] = (orgsData ?? []).map((org) => ({
		id: org.id,
		name: org.name,
		type: org.type as "buyer" | "member" | "subtier",
		parentId: org.parent_id,
		memberCount: 1, // TODO: Add member count to query
		activeSolicitations: 0, // TODO: Add solicitation count to query
	}));

	const [expandedOrgs, setExpandedOrgs] = useState<Set<string>>(
		new Set([user?.organizationId ?? ""]),
	);
	const [dialogOpen, setDialogOpen] = useState(false);

	// Edit dialog state
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
	const [editName, setEditName] = useState("");

	// Form editor state
	const [formEditorOpen, setFormEditorOpen] = useState(false);
	const [editingForm, setEditingForm] = useState<OrgForm | null>(null);
	const [isSavingForm, setIsSavingForm] = useState(false);

	// Query org forms from tRPC when an org is being edited
	const { data: orgFormsData, isLoading: isLoadingOrgForms } = useQuery(
		trpc.forms.orgForms.list.queryOptions(
			{ organizationId: editingOrg?.id ?? "" },
			{ enabled: !!editingOrg?.id },
		),
	);

	// Transform org forms data to include form template info
	const getCurrentOrgForms = useCallback((): OrgForm[] => {
		if (!orgFormsData) return [];
		return orgFormsData.map((of) => ({
			id: of.id,
			name: of.title,
			description: of.description,
			formTemplateId: of.form_template_id,
			pdfStoragePath: null, // We'll fetch from template when editing
			required: of.is_required,
		}));
	}, [orgFormsData]);

	// Mutations for form templates
	const createTemplateMutation = useMutation(
		trpc.forms.templates.create.mutationOptions({
			onError: (error) => {
				toast.error(`Failed to create form template: ${error.message}`);
			},
		}),
	);

	const updateTemplateMutation = useMutation(
		trpc.forms.templates.update.mutationOptions({
			onError: (error) => {
				toast.error(`Failed to update form template: ${error.message}`);
			},
		}),
	);

	// Mutations for org forms
	const addOrgFormMutation = useMutation(
		trpc.forms.orgForms.add.mutationOptions({
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.forms.orgForms.list.queryKey({ organizationId: editingOrg?.id ?? "" }),
				});
			},
			onError: (error) => {
				toast.error(`Failed to add form: ${error.message}`);
			},
		}),
	);

	const updateOrgFormMutation = useMutation(
		trpc.forms.orgForms.update.mutationOptions({
			onMutate: async (variables) => {
				const queryKey = trpc.forms.orgForms.list.queryKey({ organizationId: editingOrg?.id ?? "" });
				await queryClient.cancelQueries({ queryKey });
				const previousData = queryClient.getQueryData(queryKey);
				queryClient.setQueryData(queryKey, (old: typeof orgFormsData) =>
					old?.map((form) =>
						form.id === variables.id
							? { ...form, is_required: variables.isRequired ?? form.is_required, title: variables.title ?? form.title }
							: form
					)
				);
				return { previousData };
			},
			onError: (error, _variables, context) => {
				if (context?.previousData) {
					queryClient.setQueryData(
						trpc.forms.orgForms.list.queryKey({ organizationId: editingOrg?.id ?? "" }),
						context.previousData
					);
				}
				toast.error(`Failed to update form: ${error.message}`);
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.forms.orgForms.list.queryKey({ organizationId: editingOrg?.id ?? "" }),
				});
			},
		}),
	);

	const removeOrgFormMutation = useMutation(
		trpc.forms.orgForms.remove.mutationOptions({
			onMutate: async (variables) => {
				const queryKey = trpc.forms.orgForms.list.queryKey({ organizationId: editingOrg?.id ?? "" });
				await queryClient.cancelQueries({ queryKey });
				const previousData = queryClient.getQueryData(queryKey);
				queryClient.setQueryData(queryKey, (old: typeof orgFormsData) =>
					old?.filter((form) => form.id !== variables.id)
				);
				return { previousData };
			},
			onSuccess: () => {
				toast.success("Form removed");
			},
			onError: (error, _variables, context) => {
				if (context?.previousData) {
					queryClient.setQueryData(
						trpc.forms.orgForms.list.queryKey({ organizationId: editingOrg?.id ?? "" }),
						context.previousData
					);
				}
				toast.error(`Failed to remove form: ${error.message}`);
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.forms.orgForms.list.queryKey({ organizationId: editingOrg?.id ?? "" }),
				});
			},
		}),
	);

	// Required document dialog state
	const [reqDocDialogOpen, setReqDocDialogOpen] = useState(false);
	const [editingReqDocId, setEditingReqDocId] = useState<string | null>(null);
	const [reqDocName, setReqDocName] = useState("");
	const [reqDocDescription, setReqDocDescription] = useState("");
	const [reqDocRequired, setReqDocRequired] = useState(true);

	// Compliance check dialog state
	const [complianceDialogOpen, setComplianceDialogOpen] = useState(false);
	const [editingComplianceId, setEditingComplianceId] = useState<string | null>(null);
	const [complianceName, setComplianceName] = useState("");
	const [complianceDescription, setComplianceDescription] = useState("");
	const [complianceRequired, setComplianceRequired] = useState(true);

	// Fetch required documents for the editing org
	const { data: requiredDocsData, isLoading: isLoadingRequiredDocs } = useQuery(
		trpc.organizations.listRequiredDocs.queryOptions(
			{ organizationId: editingOrg?.id ?? "" },
			{ enabled: !!editingOrg?.id }
		),
	);

	// Fetch compliance checks for the editing org
	const { data: complianceChecksData, isLoading: isLoadingComplianceChecks } = useQuery(
		trpc.organizations.listComplianceChecks.queryOptions(
			{ organizationId: editingOrg?.id ?? "" },
			{ enabled: !!editingOrg?.id }
		),
	);

	// Required docs mutations
	const createRequiredDocMutation = useMutation(
		trpc.organizations.createRequiredDoc.mutationOptions({
			onSuccess: () => {
				toast.success("Required document added");
				setReqDocDialogOpen(false);
				queryClient.invalidateQueries({ queryKey: trpc.organizations.listRequiredDocs.queryKey({ organizationId: editingOrg?.id ?? "" }) });
			},
			onError: (error) => {
				toast.error(`Failed to add document: ${error.message}`);
			},
		}),
	);

	const updateRequiredDocMutation = useMutation(
		trpc.organizations.updateRequiredDoc.mutationOptions({
			onMutate: async (variables) => {
				const queryKey = trpc.organizations.listRequiredDocs.queryKey({ organizationId: editingOrg?.id ?? "" });
				await queryClient.cancelQueries({ queryKey });
				const previousData = queryClient.getQueryData(queryKey);
				queryClient.setQueryData(queryKey, (old: typeof requiredDocsData) =>
					old?.map((doc) =>
						doc.id === variables.id
							? { ...doc, is_required: variables.isRequired ?? doc.is_required, title: variables.title ?? doc.title, description: variables.description ?? doc.description }
							: doc
					)
				);
				return { previousData };
			},
			onSuccess: () => {
				toast.success("Required document updated");
				setReqDocDialogOpen(false);
			},
			onError: (error, _variables, context) => {
				if (context?.previousData) {
					queryClient.setQueryData(
						trpc.organizations.listRequiredDocs.queryKey({ organizationId: editingOrg?.id ?? "" }),
						context.previousData
					);
				}
				toast.error(`Failed to update document: ${error.message}`);
			},
			onSettled: () => {
				queryClient.invalidateQueries({ queryKey: trpc.organizations.listRequiredDocs.queryKey({ organizationId: editingOrg?.id ?? "" }) });
			},
		}),
	);

	const deleteRequiredDocMutation = useMutation(
		trpc.organizations.deleteRequiredDoc.mutationOptions({
			onMutate: async (variables) => {
				const queryKey = trpc.organizations.listRequiredDocs.queryKey({ organizationId: editingOrg?.id ?? "" });
				await queryClient.cancelQueries({ queryKey });
				const previousData = queryClient.getQueryData(queryKey);
				queryClient.setQueryData(queryKey, (old: typeof requiredDocsData) =>
					old?.filter((doc) => doc.id !== variables.id)
				);
				return { previousData };
			},
			onSuccess: () => {
				toast.success("Required document removed");
			},
			onError: (error, _variables, context) => {
				if (context?.previousData) {
					queryClient.setQueryData(
						trpc.organizations.listRequiredDocs.queryKey({ organizationId: editingOrg?.id ?? "" }),
						context.previousData
					);
				}
				toast.error(`Failed to remove document: ${error.message}`);
			},
			onSettled: () => {
				queryClient.invalidateQueries({ queryKey: trpc.organizations.listRequiredDocs.queryKey({ organizationId: editingOrg?.id ?? "" }) });
			},
		}),
	);

	// Compliance checks mutations
	const createComplianceCheckMutation = useMutation(
		trpc.organizations.createComplianceCheck.mutationOptions({
			onSuccess: () => {
				toast.success("Compliance check added");
				setComplianceDialogOpen(false);
				queryClient.invalidateQueries({ queryKey: trpc.organizations.listComplianceChecks.queryKey({ organizationId: editingOrg?.id ?? "" }) });
			},
			onError: (error) => {
				toast.error(`Failed to add compliance check: ${error.message}`);
			},
		}),
	);

	const updateComplianceCheckMutation = useMutation(
		trpc.organizations.updateComplianceCheck.mutationOptions({
			onMutate: async (variables) => {
				const queryKey = trpc.organizations.listComplianceChecks.queryKey({ organizationId: editingOrg?.id ?? "" });
				await queryClient.cancelQueries({ queryKey });
				const previousData = queryClient.getQueryData(queryKey);
				queryClient.setQueryData(queryKey, (old: typeof complianceChecksData) =>
					old?.map((check) =>
						check.id === variables.id
							? { ...check, is_required: variables.isRequired ?? check.is_required, title: variables.title ?? check.title, description: variables.description ?? check.description }
							: check
					)
				);
				return { previousData };
			},
			onSuccess: () => {
				toast.success("Compliance check updated");
				setComplianceDialogOpen(false);
			},
			onError: (error, _variables, context) => {
				if (context?.previousData) {
					queryClient.setQueryData(
						trpc.organizations.listComplianceChecks.queryKey({ organizationId: editingOrg?.id ?? "" }),
						context.previousData
					);
				}
				toast.error(`Failed to update compliance check: ${error.message}`);
			},
			onSettled: () => {
				queryClient.invalidateQueries({ queryKey: trpc.organizations.listComplianceChecks.queryKey({ organizationId: editingOrg?.id ?? "" }) });
			},
		}),
	);

	const deleteComplianceCheckMutation = useMutation(
		trpc.organizations.deleteComplianceCheck.mutationOptions({
			onMutate: async (variables) => {
				const queryKey = trpc.organizations.listComplianceChecks.queryKey({ organizationId: editingOrg?.id ?? "" });
				await queryClient.cancelQueries({ queryKey });
				const previousData = queryClient.getQueryData(queryKey);
				queryClient.setQueryData(queryKey, (old: typeof complianceChecksData) =>
					old?.filter((check) => check.id !== variables.id)
				);
				return { previousData };
			},
			onSuccess: () => {
				toast.success("Compliance check removed");
			},
			onError: (error, _variables, context) => {
				if (context?.previousData) {
					queryClient.setQueryData(
						trpc.organizations.listComplianceChecks.queryKey({ organizationId: editingOrg?.id ?? "" }),
						context.previousData
					);
				}
				toast.error(`Failed to remove compliance check: ${error.message}`);
			},
			onSettled: () => {
				queryClient.invalidateQueries({ queryKey: trpc.organizations.listComplianceChecks.queryKey({ organizationId: editingOrg?.id ?? "" }) });
			},
		}),
	);

	// Evaluation criteria state
	const [hasDefaultEvalCriteria, setHasDefaultEvalCriteria] = useState(false);
	const [editingEvalCriteriaId, setEditingEvalCriteriaId] = useState<string | null>(null);
	const [evalCriteriaTitle, setEvalCriteriaTitle] = useState("");
	const [evalCriteriaDescription, setEvalCriteriaDescription] = useState("");
	const [evalCriteriaMaxPoints, setEvalCriteriaMaxPoints] = useState(10);

	// Scoring weights for evaluation criteria (maps criterion ID to weight percentage)
	const [criteriaWeights, setCriteriaWeights] = useState<Record<string, number>>({});

	// Fetch evaluation criteria for the editing org
	const { data: evalCriteriaData } = useQuery(
		trpc.organizations.listEvaluationCriteria.queryOptions(
			{ organizationId: editingOrg?.id ?? "" },
			{ enabled: !!editingOrg?.id }
		),
	);

	// Evaluation criteria mutations
	const createEvalCriteriaMutation = useMutation(
		trpc.organizations.createEvaluationCriterion.mutationOptions({
			onSuccess: () => {
				toast.success("Evaluation criterion added");
				queryClient.invalidateQueries({ queryKey: trpc.organizations.listEvaluationCriteria.queryKey({ organizationId: editingOrg?.id ?? "" }) });
			},
			onError: (error) => {
				toast.error(`Failed to add criterion: ${error.message}`);
			},
		}),
	);

	const updateEvalCriteriaMutation = useMutation(
		trpc.organizations.updateEvaluationCriterion.mutationOptions({
			onMutate: async (variables) => {
				const queryKey = trpc.organizations.listEvaluationCriteria.queryKey({ organizationId: editingOrg?.id ?? "" });
				await queryClient.cancelQueries({ queryKey });
				const previousData = queryClient.getQueryData(queryKey);
				queryClient.setQueryData(queryKey, (old: typeof evalCriteriaData) =>
					old?.map((criterion) =>
						criterion.id === variables.id
							? { ...criterion, title: variables.title ?? criterion.title, description: variables.description ?? criterion.description, max_points: variables.maxPoints ?? criterion.max_points }
							: criterion
					)
				);
				return { previousData };
			},
			onSuccess: () => {
				toast.success("Evaluation criterion updated");
			},
			onError: (error, _variables, context) => {
				if (context?.previousData) {
					queryClient.setQueryData(
						trpc.organizations.listEvaluationCriteria.queryKey({ organizationId: editingOrg?.id ?? "" }),
						context.previousData
					);
				}
				toast.error(`Failed to update criterion: ${error.message}`);
			},
			onSettled: () => {
				queryClient.invalidateQueries({ queryKey: trpc.organizations.listEvaluationCriteria.queryKey({ organizationId: editingOrg?.id ?? "" }) });
			},
		}),
	);

	const deleteEvalCriteriaMutation = useMutation(
		trpc.organizations.deleteEvaluationCriterion.mutationOptions({
			onMutate: async (variables) => {
				const queryKey = trpc.organizations.listEvaluationCriteria.queryKey({ organizationId: editingOrg?.id ?? "" });
				await queryClient.cancelQueries({ queryKey });
				const previousData = queryClient.getQueryData(queryKey);
				queryClient.setQueryData(queryKey, (old: typeof evalCriteriaData) =>
					old?.filter((criterion) => criterion.id !== variables.id)
				);
				return { previousData };
			},
			onSuccess: () => {
				toast.success("Evaluation criterion removed");
			},
			onError: (error, _variables, context) => {
				if (context?.previousData) {
					queryClient.setQueryData(
						trpc.organizations.listEvaluationCriteria.queryKey({ organizationId: editingOrg?.id ?? "" }),
						context.previousData
					);
				}
				toast.error(`Failed to remove criterion: ${error.message}`);
			},
			onSettled: () => {
				queryClient.invalidateQueries({ queryKey: trpc.organizations.listEvaluationCriteria.queryKey({ organizationId: editingOrg?.id ?? "" }) });
			},
		}),
	);

	const buyerOrg = organizations.find((o) => o.type === "buyer");
	const memberOrgs = organizations.filter((o) => o.type === "member");
	const subtierOrgs = organizations.filter((o) => o.type === "subtier");

	const toggleExpand = (id: string) => {
		const newExpanded = new Set(expandedOrgs);
		if (newExpanded.has(id)) {
			newExpanded.delete(id);
		} else {
			newExpanded.add(id);
		}
		setExpandedOrgs(newExpanded);
	};

	const handleAddOrg = () => {
		if (!newOrgName.trim()) {
			toast.error("Organization name is required");
			return;
		}
		if (!newOrgParent) {
			toast.error("Parent organization is required");
			return;
		}

		if (newOrgType === "member") {
			createMemberMutation.mutate({
				buyerId: newOrgParent,
				name: newOrgName,
				email: newOrgEmail || undefined,
				phone: newOrgPhone || undefined,
			});
		} else {
			createSubtierMutation.mutate({
				memberId: newOrgParent,
				name: newOrgName,
				email: newOrgEmail || undefined,
				phone: newOrgPhone || undefined,
			});
		}
	};

	const handleOpenEditDialog = (org: Organization) => {
		setEditingOrg(org);
		setEditName(org.name);
		setEditDialogOpen(true);
	};

	const handleAddForm = async (formData: {
		name: string;
		description: string;
		pdfStoragePath: string | null;
		formTemplateId?: string;
		widgets?: Array<{
			fieldName: string;
			fieldLabel: string;
			fieldType: string;
			pageNumber: number;
			x: number;
			y: number;
			width: number;
			height: number;
			isRequired: boolean;
			placeholder?: string;
			description?: string;
			options?: unknown;
			fontSize?: number;
			tabOrder: number;
		}>;
	}) => {
		if (!editingOrg || !formData.pdfStoragePath) return;

		setIsSavingForm(true);
		try {
			// Create form template with PDF storage path
			const template = await createTemplateMutation.mutateAsync({
				title: formData.name,
				description: formData.description || undefined,
				pdfStoragePath: formData.pdfStoragePath,
				fieldConfig: [],
			});

			// Link to org
			await addOrgFormMutation.mutateAsync({
				organizationId: editingOrg.id,
				formTemplateId: template.id,
				title: formData.name,
				description: formData.description || undefined,
				isRequired: false,
				sortOrder: orgFormsData?.length ?? 0,
			});
		} catch (error) {
			console.error("Error adding form:", error);
			toast.error(error instanceof Error ? error.message : "Failed to add form");
			throw error; // Re-throw so PdfFormEditorV2 knows it failed
		} finally {
			setIsSavingForm(false);
		}
	};

	const handleUpdateForm = async (formData: {
		name: string;
		description: string;
		pdfStoragePath: string | null;
		formTemplateId?: string;
		widgets?: Array<{
			fieldName: string;
			fieldLabel: string;
			fieldType: string;
			pageNumber: number;
			x: number;
			y: number;
			width: number;
			height: number;
			isRequired: boolean;
			placeholder?: string;
			description?: string;
			options?: unknown;
			fontSize?: number;
			tabOrder: number;
		}>;
	}) => {
		if (!editingOrg || !editingForm) return;

		setIsSavingForm(true);
		try {
			// If there's a new/changed PDF storage path, update the template
			if (formData.pdfStoragePath) {
				// Update template with new PDF storage path
				await updateTemplateMutation.mutateAsync({
					id: editingForm.formTemplateId,
					title: formData.name,
					pdfStoragePath: formData.pdfStoragePath,
				});
			} else {
				// Just update the title
				await updateTemplateMutation.mutateAsync({
					id: editingForm.formTemplateId,
					title: formData.name,
				});
			}

			// Update the org form title and description if changed
			const updates: { id: string; title?: string; description?: string } = { id: editingForm.id };
			if (formData.name !== editingForm.name) {
				updates.title = formData.name;
			}
			if (formData.description !== (editingForm.description ?? "")) {
				updates.description = formData.description || undefined;
			}
			if (Object.keys(updates).length > 1) {
				await updateOrgFormMutation.mutateAsync(updates);
			}

			queryClient.invalidateQueries({
				queryKey: trpc.forms.orgForms.list.queryKey({ organizationId: editingOrg.id }),
			});
		} catch (error) {
			console.error("Error updating form:", error);
			toast.error(error instanceof Error ? error.message : "Failed to update form");
			throw error; // Re-throw so PdfFormEditorV2 knows it failed
		} finally {
			setIsSavingForm(false);
		}
	};

	const handleDeleteForm = (formId: string) => {
		if (!editingOrg) return;
		removeOrgFormMutation.mutate({ id: formId });
	};

	const handleToggleRequired = (formId: string) => {
		if (!editingOrg) return;
		const form = orgFormsData?.find((f) => f.id === formId);
		if (form) {
			updateOrgFormMutation.mutate({
				id: formId,
				isRequired: !form.is_required,
			});
		}
	};

	// Handle opening form editor for editing an existing form
	const handleEditForm = async (form: OrgForm) => {
		try {
			// Fetch the form template to get the pdf_storage_path
			const template = await trpcClient.forms.templates.get.query({
				id: form.formTemplateId,
			});

			// Update form with pdf_storage_path from template
			setEditingForm({
				...form,
				pdfStoragePath: template.pdf_storage_path ?? null,
			});

			// Close the edit dialog before opening form editor to avoid z-index conflicts
			setEditDialogOpen(false);
			setFormEditorOpen(true);
		} catch (error) {
			console.error("Error loading form:", error);
			toast.error("Failed to load form for editing");
		}
	};

	// Required documents helpers
	const getCurrentOrgRequiredDocs = () => {
		return (requiredDocsData ?? []).map((d) => ({
			id: d.id,
			name: d.title,
			description: d.description ?? "",
			required: d.is_required,
		}));
	};

	const handleOpenReqDocDialog = (doc?: { id: string; name: string; description: string; required: boolean }) => {
		if (doc) {
			setEditingReqDocId(doc.id);
			setReqDocName(doc.name);
			setReqDocDescription(doc.description);
			setReqDocRequired(doc.required);
		} else {
			setEditingReqDocId(null);
			setReqDocName("");
			setReqDocDescription("");
			setReqDocRequired(true);
		}
		setReqDocDialogOpen(true);
	};

	const handleSaveReqDoc = () => {
		if (!editingOrg || !reqDocName.trim()) return;

		if (editingReqDocId) {
			// Update existing
			updateRequiredDocMutation.mutate({
				id: editingReqDocId,
				title: reqDocName,
				description: reqDocDescription,
				isRequired: reqDocRequired,
			});
		} else {
			// Add new
			createRequiredDocMutation.mutate({
				organizationId: editingOrg.id,
				title: reqDocName,
				description: reqDocDescription,
				isRequired: reqDocRequired,
			});
		}
	};

	const handleDeleteReqDoc = (docId: string) => {
		deleteRequiredDocMutation.mutate({ id: docId });
	};

	const handleToggleReqDocRequired = (docId: string) => {
		const doc = requiredDocsData?.find((d) => d.id === docId);
		if (doc) {
			updateRequiredDocMutation.mutate({
				id: docId,
				isRequired: !doc.is_required,
			});
		}
	};

	// Compliance checks helpers
	const getCurrentOrgComplianceChecks = () => {
		return (complianceChecksData ?? []).map((c) => ({
			id: c.id,
			name: c.title,
			description: c.description ?? "",
			required: c.is_required,
		}));
	};

	const handleOpenComplianceDialog = (check?: { id: string; name: string; description: string; required: boolean }) => {
		if (check) {
			setEditingComplianceId(check.id);
			setComplianceName(check.name);
			setComplianceDescription(check.description);
			setComplianceRequired(check.required);
		} else {
			setEditingComplianceId(null);
			setComplianceName("");
			setComplianceDescription("");
			setComplianceRequired(true);
		}
		setComplianceDialogOpen(true);
	};

	const handleSaveCompliance = () => {
		if (!editingOrg || !complianceName.trim()) return;

		if (editingComplianceId) {
			// Update existing
			updateComplianceCheckMutation.mutate({
				id: editingComplianceId,
				title: complianceName,
				description: complianceDescription,
				isRequired: complianceRequired,
			});
		} else {
			// Add new
			createComplianceCheckMutation.mutate({
				organizationId: editingOrg.id,
				title: complianceName,
				description: complianceDescription,
				isRequired: complianceRequired,
			});
		}
	};

	const handleDeleteCompliance = (checkId: string) => {
		deleteComplianceCheckMutation.mutate({ id: checkId });
	};

	const handleToggleComplianceRequired = (checkId: string) => {
		const check = complianceChecksData?.find((c) => c.id === checkId);
		if (check) {
			updateComplianceCheckMutation.mutate({
				id: checkId,
				isRequired: !check.is_required,
			});
		}
	};

	// Evaluation criteria helpers
	const getCurrentOrgEvalCriteria = () => {
		return (evalCriteriaData ?? []).map((c) => ({
			id: c.id,
			title: c.title,
			description: c.description ?? "",
			maxPoints: c.max_points,
		}));
	};

	// Initialize hasDefaultEvalCriteria based on whether criteria exist
	useEffect(() => {
		if (evalCriteriaData && evalCriteriaData.length > 0) {
			setHasDefaultEvalCriteria(true);
		}
	}, [evalCriteriaData]);

	const handleEditEvalCriterion = (criterion: { id: string; title: string; description: string; maxPoints: number }) => {
		setEditingEvalCriteriaId(criterion.id);
		setEvalCriteriaTitle(criterion.title);
		setEvalCriteriaDescription(criterion.description);
		setEvalCriteriaMaxPoints(criterion.maxPoints);
	};

	const handleAddEvalCriterion = () => {
		if (!editingOrg || !evalCriteriaTitle.trim()) return;
		createEvalCriteriaMutation.mutate({
			organizationId: editingOrg.id,
			title: evalCriteriaTitle,
			description: evalCriteriaDescription,
			maxPoints: evalCriteriaMaxPoints,
		});
		// Reset form
		setEvalCriteriaTitle("");
		setEvalCriteriaDescription("");
		setEvalCriteriaMaxPoints(10);
	};

	const handleUpdateEvalCriterion = () => {
		if (!editingEvalCriteriaId || !evalCriteriaTitle.trim()) return;
		updateEvalCriteriaMutation.mutate({
			id: editingEvalCriteriaId,
			title: evalCriteriaTitle,
			description: evalCriteriaDescription,
			maxPoints: evalCriteriaMaxPoints,
		});
		// Reset form
		setEditingEvalCriteriaId(null);
		setEvalCriteriaTitle("");
		setEvalCriteriaDescription("");
		setEvalCriteriaMaxPoints(10);
	};

	const handleCancelEditCriterion = () => {
		setEditingEvalCriteriaId(null);
		setEvalCriteriaTitle("");
		setEvalCriteriaDescription("");
		setEvalCriteriaMaxPoints(10);
	};

	const handleDeleteEvalCriteria = (criterionId: string) => {
		deleteEvalCriteriaMutation.mutate({ id: criterionId });
	};

	// Mutation for updating weights
	const updateWeightsMutation = useMutation(
		trpc.organizations.updateEvaluationCriteriaWeights.mutationOptions({
			onSuccess: () => {
				toast.success("Scoring weights saved");
				queryClient.invalidateQueries({ queryKey: trpc.organizations.listEvaluationCriteria.queryKey({ organizationId: editingOrg?.id ?? "" }) });
			},
			onError: (error) => {
				toast.error(`Failed to save weights: ${error.message}`);
			},
		}),
	);

	// Initialize weights from database when criteria change
	// Auto-rebalance to 100% when criteria are added or removed
	useEffect(() => {
		if (evalCriteriaData && evalCriteriaData.length > 0) {
			const weightsFromDb: Record<string, number> = {};
			let totalFromDb = 0;
			for (const criterion of evalCriteriaData) {
				const weight = criterion.weight ?? 0;
				weightsFromDb[criterion.id] = weight;
				totalFromDb += weight;
			}

			// If weights don't sum to 100, redistribute equally
			if (totalFromDb !== 100) {
				const numCriteria = evalCriteriaData.length;
				const equalWeight = Math.floor(100 / numCriteria);
				const remainder = 100 - equalWeight * numCriteria;
				const rebalancedWeights: Record<string, number> = {};
				evalCriteriaData.forEach((c, index) => {
					rebalancedWeights[c.id] = equalWeight + (index === 0 ? remainder : 0);
				});
				setCriteriaWeights(rebalancedWeights);
			} else {
				setCriteriaWeights(weightsFromDb);
			}
		} else {
			setCriteriaWeights({});
		}
	}, [evalCriteriaData]);

	const handleWeightChange = (id: string, value: number) => {
		// Simply update the weight for this criterion without affecting others
		setCriteriaWeights(prev => ({ ...prev, [id]: value }));
	};

	const handleSaveWeights = () => {
		if (!editingOrg) return;
		const weights = Object.entries(criteriaWeights).map(([id, weight]) => ({ id, weight }));
		updateWeightsMutation.mutate({
			organizationId: editingOrg.id,
			weights,
		});
	};

	const totalEvalPoints = getCurrentOrgEvalCriteria().reduce((sum, c) => sum + c.maxPoints, 0);
	const criteria = getCurrentOrgEvalCriteria();
	const totalWeight = criteria.reduce((sum, c) => sum + (criteriaWeights[c.id] ?? 0), 0);

	const renderOrgNode = (org: Organization, level: number = 0) => {
		const children =
			level === 0
				? memberOrgs.filter((m) => m.parentId === org.id)
				: subtierOrgs.filter((s) => s.parentId === org.id);
		const hasChildren = children.length > 0;
		const isExpanded = expandedOrgs.has(org.id);

		return (
			<div key={org.id}>
				<div
					className={`flex items-center justify-between p-4 border rounded-lg mb-2 ${
						level === 0 ? "bg-primary/5 border-primary/20" : "bg-background"
					}`}
					style={{ marginLeft: level * 24 }}
				>
					<div className="flex items-center gap-3">
						{hasChildren && (
							<button
								type="button"
								onClick={() => toggleExpand(org.id)}
								className="p-1 hover:bg-muted rounded"
							>
								{isExpanded ? (
									<ChevronDown className="h-4 w-4" />
								) : (
									<ChevronRight className="h-4 w-4" />
								)}
							</button>
						)}
						{!hasChildren && <div className="w-6" />}
						<Building2 className="h-5 w-5 text-muted-foreground" />
						<div>
							<p className="font-medium">{org.name}</p>
							<p className="text-sm text-muted-foreground">
								{org.type === "buyer"
									? "Buyer Organization"
									: org.type === "member"
										? "Member Organization"
										: "Subtier"}
							</p>
						</div>
					</div>
					<div className="flex items-center gap-4">
						<div className="flex items-center gap-4 text-sm text-muted-foreground">
							<span className="flex items-center gap-1">
								<Users className="h-4 w-4" />
								{org.memberCount}
							</span>
							<span className="flex items-center gap-1">
								<FileText className="h-4 w-4" />
								{org.activeSolicitations}
							</span>
						</div>
						{hasPermission("admin") && (
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button variant="ghost" size="icon">
										<MoreHorizontal className="h-4 w-4" />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end">
									<DropdownMenuItem onClick={() => handleOpenEditDialog(org)}>
										<Edit className="mr-2 h-4 w-4" />
										Edit
									</DropdownMenuItem>
									{org.type !== "buyer" && (
										<DropdownMenuItem
											className="text-red-600"
											onClick={() => toast.success("Organization removed")}
										>
											<Trash2 className="mr-2 h-4 w-4" />
											Remove
										</DropdownMenuItem>
									)}
								</DropdownMenuContent>
							</DropdownMenu>
						)}
					</div>
				</div>
				{isExpanded && children.map((child) => renderOrgNode(child, level + 1))}
			</div>
		);
	};

	return (
		<DashboardLayout>
			<div className="space-y-6">
				<div className="flex items-center justify-between">
					<div>
						<h1 className="text-3xl font-bold tracking-tight">Organizations</h1>
						<p className="text-muted-foreground">
							Manage your organization hierarchy
						</p>
					</div>
					{hasPermission("admin") && (
						<Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
							<DialogTrigger asChild>
								<Button>
									<Plus className="mr-2 h-4 w-4" />
									Add Organization
								</Button>
							</DialogTrigger>
							<DialogContent>
								<DialogHeader>
									<DialogTitle>Add Organization</DialogTitle>
									<DialogDescription>
										Add a new member organization or subtier
									</DialogDescription>
								</DialogHeader>
								<div className="space-y-4 py-4">
									<div className="space-y-2">
										<Label htmlFor="orgType">Organization Type</Label>
										<Select
											value={newOrgType}
											onValueChange={(value) => {
												setNewOrgType(value as "member" | "subtier");
												setNewOrgParent(""); // Reset parent when type changes
											}}
										>
											<SelectTrigger>
												<SelectValue placeholder="Select type" />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="member">
													Member Organization
												</SelectItem>
												<SelectItem value="subtier">Subtier</SelectItem>
											</SelectContent>
										</Select>
									</div>
									<div className="space-y-2">
										<Label htmlFor="parentOrg">Parent Organization</Label>
										<Select
											value={newOrgParent}
											onValueChange={setNewOrgParent}
										>
											<SelectTrigger>
												<SelectValue placeholder="Select parent" />
											</SelectTrigger>
											<SelectContent>
												{newOrgType === "member" ? (
													buyerOrg && (
														<SelectItem value={buyerOrg.id}>{buyerOrg.name}</SelectItem>
													)
												) : (
													memberOrgs.map((org) => (
														<SelectItem key={org.id} value={org.id}>
															{org.name}
														</SelectItem>
													))
												)}
											</SelectContent>
										</Select>
									</div>
									<div className="space-y-2">
										<Label htmlFor="name">Organization Name</Label>
										<Input
											id="name"
											placeholder="Enter organization name"
											value={newOrgName}
											onChange={(e) => setNewOrgName(e.target.value)}
										/>
									</div>
									<div className="space-y-2">
										<Label htmlFor="email">Contact Email</Label>
										<Input
											id="email"
											type="email"
											placeholder="contact@example.gov"
											value={newOrgEmail}
											onChange={(e) => setNewOrgEmail(e.target.value)}
										/>
									</div>
									<div className="space-y-2">
										<Label htmlFor="phone">Phone</Label>
										<Input
											id="phone"
											placeholder="(555) 123-4567"
											value={newOrgPhone}
											onChange={(e) => setNewOrgPhone(e.target.value)}
										/>
									</div>
								</div>
								<DialogFooter>
									<Button
										variant="outline"
										onClick={() => {
											setDialogOpen(false);
											resetNewOrgForm();
										}}
									>
										Cancel
									</Button>
									<Button
										onClick={handleAddOrg}
										disabled={createMemberMutation.isPending || createSubtierMutation.isPending}
									>
										{(createMemberMutation.isPending || createSubtierMutation.isPending) ? "Adding..." : "Add Organization"}
									</Button>
								</DialogFooter>
							</DialogContent>
						</Dialog>
					)}
				</div>

	
				{/* Org Hierarchy */}
				<Card>
					<CardHeader>
						<CardTitle>Organization Hierarchy</CardTitle>
						<CardDescription>
							Your buyer organization and its members
						</CardDescription>
					</CardHeader>
					<CardContent>
						{isLoading ? (
							<div className="flex items-center justify-center py-8">
								<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
							</div>
						) : buyerOrg ? (
							renderOrgNode(buyerOrg)
						) : (
							<div className="text-center py-8 text-muted-foreground">
								<Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
								<p>No organizations found</p>
							</div>
						)}
					</CardContent>
				</Card>

				{/* Edit Organization Dialog */}
				<Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
					<DialogContent className="sm:max-w-4xl">
						<DialogHeader>
							<DialogTitle>Edit Organization</DialogTitle>
							<DialogDescription>
								Manage vendor forms, requirements, and evaluation criteria
							</DialogDescription>
						</DialogHeader>
						<ScrollArea className="max-h-[70vh] pr-4">
							<div className="space-y-6 py-4">
								<div className="space-y-4">
									<div className="flex items-center justify-between">
										<div>
											<Label>Vendor Forms</Label>
											<p className="text-sm text-muted-foreground">
												Forms that vendors must complete for this organization
											</p>
										</div>
										<Button
											variant="outline"
											size="sm"
											disabled={isSavingForm || removeOrgFormMutation.isPending || updateOrgFormMutation.isPending}
											onClick={() => {
												setEditingForm(null);
												setEditDialogOpen(false);
												setFormEditorOpen(true);
											}}
										>
											<Plus className="mr-2 h-4 w-4" />
											Add Form
										</Button>
									</div>

									{isLoadingOrgForms ? (
									<div className="flex items-center justify-center py-8 border rounded-lg bg-muted/30">
										<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
									</div>
								) : getCurrentOrgForms().length > 0 ? (
										<div className="space-y-2">
											{getCurrentOrgForms().map((form) => (
												<div
													key={form.id}
													className="flex items-start justify-between p-3 border rounded-lg"
												>
													<div className="flex items-start gap-3">
														<FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
														<div>
															<div className="flex items-center gap-2">
																<span className="font-medium">{form.name}</span>
																<Badge
																	variant={
																		form.required ? "default" : "secondary"
																	}
																>
																	{form.required ? "Required" : "Optional"}
																</Badge>
															</div>
															{form.description && (
																<p className="text-sm text-muted-foreground mt-1">
																	{form.description}
																</p>
															)}
														</div>
													</div>
													<div className="flex items-center gap-2">
														<div className="flex items-center gap-2 mr-2">
															<Label
																htmlFor={`required-${form.id}`}
																className="text-sm text-muted-foreground"
															>
																Required
															</Label>
															<Switch
																id={`required-${form.id}`}
																checked={form.required}
																disabled={updateOrgFormMutation.isPending || removeOrgFormMutation.isPending || isSavingForm}
																onCheckedChange={() =>
																	handleToggleRequired(form.id)
																}
															/>
														</div>
														<Button
															variant="ghost"
															size="sm"
															disabled={updateOrgFormMutation.isPending || removeOrgFormMutation.isPending || isSavingForm}
															onClick={() => handleEditForm(form)}
														>
															<Pencil className="h-4 w-4" />
														</Button>
														<Button
															variant="ghost"
															size="sm"
															disabled={updateOrgFormMutation.isPending || removeOrgFormMutation.isPending || isSavingForm}
															onClick={() => handleDeleteForm(form.id)}
														>
															{removeOrgFormMutation.isPending ? (
																<Loader2 className="h-4 w-4 animate-spin" />
															) : (
																<Trash2 className="h-4 w-4" />
															)}
														</Button>
													</div>
												</div>
											))}
										</div>
									) : (
										<div className="text-center py-8 border rounded-lg bg-muted/30">
											<FileText className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
											<p className="text-muted-foreground">
												No forms configured
											</p>
											<p className="text-sm text-muted-foreground">
												Add forms that vendors must complete for this
												organization
											</p>
										</div>
									)}
								</div>

								<Separator />

								{/* Required Documents Section */}
								<div className="space-y-4">
									<div className="flex items-center justify-between">
										<div>
											<Label>Required Documents</Label>
											<p className="text-sm text-muted-foreground">
												Documents that vendors must upload during onboarding
											</p>
										</div>
										<Button
											variant="outline"
											size="sm"
											disabled={createRequiredDocMutation.isPending || updateRequiredDocMutation.isPending || deleteRequiredDocMutation.isPending}
											onClick={() => handleOpenReqDocDialog()}
										>
											<Plus className="mr-2 h-4 w-4" />
											Add Document
										</Button>
									</div>

									{isLoadingRequiredDocs ? (
									<div className="flex items-center justify-center py-8 border rounded-lg bg-muted/30">
										<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
									</div>
								) : getCurrentOrgRequiredDocs().length > 0 ? (
										<div className="space-y-2">
											{getCurrentOrgRequiredDocs().map((doc) => (
												<div
													key={doc.id}
													className="flex items-center justify-between p-3 border rounded-lg"
												>
													<div className="flex items-center gap-3">
														<Upload className="h-5 w-5 text-muted-foreground" />
														<div>
															<span className="font-medium">{doc.name}</span>
															{doc.description && (
																<p className="text-sm text-muted-foreground">
																	{doc.description}
																</p>
															)}
															<div className="flex items-center gap-2 mt-1">
																<Badge
																	variant={
																		doc.required ? "default" : "secondary"
																	}
																>
																	{doc.required ? "Required" : "Optional"}
																</Badge>
															</div>
														</div>
													</div>
													<div className="flex items-center gap-2">
														<div className="flex items-center gap-2 mr-2">
															<Label
																htmlFor={`req-doc-${doc.id}`}
																className="text-sm text-muted-foreground"
															>
																Required
															</Label>
															<Switch
																id={`req-doc-${doc.id}`}
																checked={doc.required}
																disabled={createRequiredDocMutation.isPending || updateRequiredDocMutation.isPending || deleteRequiredDocMutation.isPending}
																onCheckedChange={() =>
																	handleToggleReqDocRequired(doc.id)
																}
															/>
														</div>
														<Button
															variant="ghost"
															size="sm"
															disabled={createRequiredDocMutation.isPending || updateRequiredDocMutation.isPending || deleteRequiredDocMutation.isPending}
															onClick={() => handleOpenReqDocDialog(doc)}
														>
															<Pencil className="h-4 w-4" />
														</Button>
														<Button
															variant="ghost"
															size="sm"
															disabled={createRequiredDocMutation.isPending || updateRequiredDocMutation.isPending || deleteRequiredDocMutation.isPending}
															onClick={() => handleDeleteReqDoc(doc.id)}
														>
															{deleteRequiredDocMutation.isPending ? (
																<Loader2 className="h-4 w-4 animate-spin" />
															) : (
																<Trash2 className="h-4 w-4" />
															)}
														</Button>
													</div>
												</div>
											))}
										</div>
									) : (
										<div className="text-center py-8 border rounded-lg bg-muted/30">
											<Upload className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
											<p className="text-muted-foreground">
												No required documents configured
											</p>
											<p className="text-sm text-muted-foreground">
												Add documents that vendors must upload during onboarding
											</p>
										</div>
									)}
								</div>

								<Separator />

								{/* Compliance Checks Section */}
								<div className="space-y-4">
									<div className="flex items-center justify-between">
										<div>
											<Label>Compliance Checks</Label>
											<p className="text-sm text-muted-foreground">
												Requirements vendors must meet (certifications,
												registrations, etc.)
											</p>
										</div>
										<Button
											variant="outline"
											size="sm"
											disabled={createComplianceCheckMutation.isPending || updateComplianceCheckMutation.isPending || deleteComplianceCheckMutation.isPending}
											onClick={() => handleOpenComplianceDialog()}
										>
											<Plus className="mr-2 h-4 w-4" />
											Add Check
										</Button>
									</div>

									{isLoadingComplianceChecks ? (
									<div className="flex items-center justify-center py-8 border rounded-lg bg-muted/30">
										<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
									</div>
								) : getCurrentOrgComplianceChecks().length > 0 ? (
										<div className="space-y-2">
											{getCurrentOrgComplianceChecks().map((check) => (
												<div
													key={check.id}
													className="flex items-center justify-between p-3 border rounded-lg"
												>
													<div className="flex items-center gap-3">
														<ClipboardCheck className="h-5 w-5 text-muted-foreground" />
														<div>
															<span className="font-medium">{check.name}</span>
															{check.description && (
																<p className="text-sm text-muted-foreground">
																	{check.description}
																</p>
															)}
															<div className="flex items-center gap-2 mt-1">
																<Badge
																	variant={
																		check.required ? "default" : "secondary"
																	}
																>
																	{check.required ? "Required" : "Optional"}
																</Badge>
															</div>
														</div>
													</div>
													<div className="flex items-center gap-2">
														<div className="flex items-center gap-2 mr-2">
															<Label
																htmlFor={`compliance-${check.id}`}
																className="text-sm text-muted-foreground"
															>
																Required
															</Label>
															<Switch
																id={`compliance-${check.id}`}
																checked={check.required}
																disabled={createComplianceCheckMutation.isPending || updateComplianceCheckMutation.isPending || deleteComplianceCheckMutation.isPending}
																onCheckedChange={() =>
																	handleToggleComplianceRequired(check.id)
																}
															/>
														</div>
														<Button
															variant="ghost"
															size="sm"
															disabled={createComplianceCheckMutation.isPending || updateComplianceCheckMutation.isPending || deleteComplianceCheckMutation.isPending}
															onClick={() => handleOpenComplianceDialog(check)}
														>
															<Pencil className="h-4 w-4" />
														</Button>
														<Button
															variant="ghost"
															size="sm"
															disabled={createComplianceCheckMutation.isPending || updateComplianceCheckMutation.isPending || deleteComplianceCheckMutation.isPending}
															onClick={() => handleDeleteCompliance(check.id)}
														>
															{deleteComplianceCheckMutation.isPending ? (
																<Loader2 className="h-4 w-4 animate-spin" />
															) : (
																<Trash2 className="h-4 w-4" />
															)}
														</Button>
													</div>
												</div>
											))}
										</div>
									) : (
										<div className="text-center py-8 border rounded-lg bg-muted/30">
											<ClipboardCheck className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
											<p className="text-muted-foreground">
												No compliance checks configured
											</p>
											<p className="text-sm text-muted-foreground">
												Add requirements vendors must meet for this organization
											</p>
										</div>
									)}
								</div>

								<Separator />

								{/* Evaluation Criteria Section */}
								<div className="space-y-4">
									{/* Has Default Evaluation Criteria Checkbox */}
									<div className="flex items-center space-x-2 p-4 border rounded-lg bg-muted/30">
										<Checkbox
											id="hasDefaultEvalCriteria"
											checked={hasDefaultEvalCriteria}
											onCheckedChange={(checked) => setHasDefaultEvalCriteria(checked as boolean)}
										/>
										<div className="grid gap-1.5 leading-none">
											<Label
												htmlFor="hasDefaultEvalCriteria"
												className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
											>
												Has default evaluation criteria
											</Label>
											<p className="text-sm text-muted-foreground">
												{hasDefaultEvalCriteria
													? "Solicitations can inherit these evaluation criteria. Total points: " + totalEvalPoints
													: "Check to define default criteria for evaluating vendor bids."}
											</p>
										</div>
									</div>

									{hasDefaultEvalCriteria && (
										<>
											{/* Evaluation Rubric Card */}
											<div className="border rounded-lg p-4 space-y-4">
												<div className="flex items-center gap-2">
													<Target className="h-5 w-5" />
													<h4 className="font-medium">Evaluation Rubric</h4>
												</div>

												{/* Add/Edit Criterion Form */}
												<div className="border rounded-lg p-4 space-y-4 bg-muted/30">
													<h4 className="font-medium">
														{editingEvalCriteriaId ? "Edit Criterion" : "Add New Criterion"}
													</h4>
													<div className="grid gap-4 md:grid-cols-2">
														<div className="space-y-2">
															<Label htmlFor="criterionTitle">Title *</Label>
															<Input
																id="criterionTitle"
																placeholder="e.g., Technical Approach"
																value={evalCriteriaTitle}
																onChange={(e) => setEvalCriteriaTitle(e.target.value)}
															/>
														</div>
														<div className="space-y-2">
															<Label htmlFor="criterionPoints">Max Points</Label>
															<Input
																id="criterionPoints"
																type="number"
																min={1}
																max={100}
																value={evalCriteriaMaxPoints}
																onChange={(e) => setEvalCriteriaMaxPoints(Number(e.target.value))}
															/>
														</div>
													</div>
													<div className="space-y-2">
														<Label htmlFor="criterionDescription">Description</Label>
														<Textarea
															id="criterionDescription"
															placeholder="Describe what evaluators should look for..."
															value={evalCriteriaDescription}
															onChange={(e) => setEvalCriteriaDescription(e.target.value)}
														/>
													</div>
													<div className="flex gap-2">
														{editingEvalCriteriaId ? (
															<>
																<Button
																	onClick={handleUpdateEvalCriterion}
																	disabled={!evalCriteriaTitle.trim() || updateEvalCriteriaMutation.isPending}
																>
																	<Save className="mr-2 h-4 w-4" />
																	{updateEvalCriteriaMutation.isPending ? "Saving..." : "Update Criterion"}
																</Button>
																<Button variant="outline" onClick={handleCancelEditCriterion}>
																	Cancel
																</Button>
															</>
														) : (
															<Button
																onClick={handleAddEvalCriterion}
																disabled={!evalCriteriaTitle.trim() || createEvalCriteriaMutation.isPending}
															>
																<Plus className="mr-2 h-4 w-4" />
																{createEvalCriteriaMutation.isPending ? "Adding..." : "Add Criterion"}
															</Button>
														)}
													</div>
												</div>

												{/* Criteria List */}
												<div className="space-y-3">
													{getCurrentOrgEvalCriteria().map((criterion, index) => (
														<div
															key={criterion.id}
															className="border rounded-lg p-4 flex items-start justify-between gap-4"
														>
															<div className="flex-1">
																<div className="flex items-center gap-2 flex-wrap">
																	<Badge variant="outline">{index + 1}</Badge>
																	<h4 className="font-medium">{criterion.title}</h4>
																	<Badge>{criterion.maxPoints} pts</Badge>
																</div>
																{criterion.description && (
																	<p className="text-sm text-muted-foreground mt-1">
																		{criterion.description}
																	</p>
																)}
															</div>
															<div className="flex gap-1">
																<Button
																	variant="ghost"
																	size="icon"
																	onClick={() => handleEditEvalCriterion(criterion)}
																>
																	<Pencil className="h-4 w-4" />
																</Button>
																<Button
																	variant="ghost"
																	size="icon"
																	onClick={() => handleDeleteEvalCriteria(criterion.id)}
																	disabled={deleteEvalCriteriaMutation.isPending}
																>
																	{deleteEvalCriteriaMutation.isPending ? (
																		<Loader2 className="h-4 w-4 animate-spin" />
																	) : (
																		<Trash2 className="h-4 w-4" />
																	)}
																</Button>
															</div>
														</div>
													))}
													{getCurrentOrgEvalCriteria().length === 0 && (
														<div className="text-center py-8 text-muted-foreground">
															No evaluation criteria defined. Add at least one criterion above.
														</div>
													)}
												</div>
											</div>

											{/* Scoring Weights Card */}
											{getCurrentOrgEvalCriteria().length > 0 && (
												<div className="border rounded-lg p-4 space-y-4">
													<div className="flex items-center justify-between">
														<div className="flex items-center gap-2">
															<Scale className="h-5 w-5" />
															<h4 className="font-medium">Scoring Weights</h4>
														</div>
														<Button
															size="sm"
															onClick={handleSaveWeights}
															disabled={updateWeightsMutation.isPending || totalWeight !== 100}
														>
															{updateWeightsMutation.isPending ? (
																<Loader2 className="mr-2 h-4 w-4 animate-spin" />
															) : (
																<Save className="mr-2 h-4 w-4" />
															)}
															Save Weights
														</Button>
													</div>
													<p className="text-sm text-muted-foreground">
														Adjust the relative importance of each criterion. Total: {totalWeight}%
													</p>
													<div className="space-y-6">
														{getCurrentOrgEvalCriteria().map((criterion) => (
															<div key={criterion.id} className="space-y-2">
																<div className="flex items-center justify-between">
																	<Label>{criterion.title}</Label>
																	<span className="text-sm font-medium">{criteriaWeights[criterion.id] ?? 0}%</span>
																</div>
																<Slider
																	value={[criteriaWeights[criterion.id] ?? 0]}
																	onValueChange={([value]) => handleWeightChange(criterion.id, value)}
																	max={100}
																	step={1}
																/>
															</div>
														))}
														{totalWeight !== 100 && (
															<p className="text-sm text-destructive">
																Weights must total 100% to save. Currently at {totalWeight}%.
															</p>
														)}
													</div>
												</div>
											)}
										</>
									)}
								</div>
							</div>
						</ScrollArea>
					</DialogContent>
				</Dialog>

				{/* Required Document Dialog */}
				<Dialog open={reqDocDialogOpen} onOpenChange={setReqDocDialogOpen}>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>
								{editingReqDocId
									? "Edit Required Document"
									: "Add Required Document"}
							</DialogTitle>
							<DialogDescription>
								Define a document that vendors must upload during onboarding
							</DialogDescription>
						</DialogHeader>
						<div className="space-y-4 py-4">
							<div className="space-y-2">
								<Label htmlFor="req-doc-name">Document Title</Label>
								<Input
									id="req-doc-name"
									value={reqDocName}
									onChange={(e) => setReqDocName(e.target.value)}
									placeholder="e.g., Business License"
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="req-doc-desc">Description</Label>
								<Input
									id="req-doc-desc"
									value={reqDocDescription}
									onChange={(e) => setReqDocDescription(e.target.value)}
									placeholder="e.g., Current business license for your jurisdiction"
								/>
							</div>
							<div className="flex items-center gap-2">
								<Switch
									id="req-doc-required"
									checked={reqDocRequired}
									onCheckedChange={setReqDocRequired}
								/>
								<Label htmlFor="req-doc-required">
									Required for onboarding
								</Label>
							</div>
						</div>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => setReqDocDialogOpen(false)}
							>
								Cancel
							</Button>
							<Button
								onClick={handleSaveReqDoc}
								disabled={!reqDocName.trim() || createRequiredDocMutation.isPending || updateRequiredDocMutation.isPending}
							>
								{createRequiredDocMutation.isPending || updateRequiredDocMutation.isPending
									? "Saving..."
									: editingReqDocId ? "Save Changes" : "Add Document"}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* Compliance Check Dialog */}
				<Dialog
					open={complianceDialogOpen}
					onOpenChange={setComplianceDialogOpen}
				>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>
								{editingComplianceId
									? "Edit Compliance Check"
									: "Add Compliance Check"}
							</DialogTitle>
							<DialogDescription>
								Define a requirement that vendors must meet
							</DialogDescription>
						</DialogHeader>
						<div className="space-y-4 py-4">
							<div className="space-y-2">
								<Label htmlFor="compliance-name">Check Title</Label>
								<Input
									id="compliance-name"
									value={complianceName}
									onChange={(e) => setComplianceName(e.target.value)}
									placeholder="e.g., SAM.gov Registration"
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="compliance-desc">Description</Label>
								<Input
									id="compliance-desc"
									value={complianceDescription}
									onChange={(e) => setComplianceDescription(e.target.value)}
									placeholder="e.g., Must be registered in SAM.gov with active status"
								/>
							</div>
							<div className="flex items-center gap-2">
								<Switch
									id="compliance-required"
									checked={complianceRequired}
									onCheckedChange={setComplianceRequired}
								/>
								<Label htmlFor="compliance-required">
									Required for vendors
								</Label>
							</div>
						</div>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => setComplianceDialogOpen(false)}
							>
								Cancel
							</Button>
							<Button
								onClick={handleSaveCompliance}
								disabled={!complianceName.trim() || createComplianceCheckMutation.isPending || updateComplianceCheckMutation.isPending}
							>
								{createComplianceCheckMutation.isPending || updateComplianceCheckMutation.isPending
									? "Saving..."
									: editingComplianceId ? "Save Changes" : "Add Check"}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* PDF Form Editor */}
				<PdfFormEditorV2
					open={formEditorOpen}
					onClose={() => {
						setFormEditorOpen(false);
						setEditingForm(null);
						// Re-open the edit dialog when returning from form editor
						setEditDialogOpen(true);
					}}
					onSave={async (formData) => {
						if (editingForm) {
							await handleUpdateForm(formData);
						} else {
							await handleAddForm(formData);
						}
					}}
					initialPdfStoragePath={editingForm?.pdfStoragePath || null}
					formName={editingForm?.name || ""}
					formDescription={editingForm?.description || ""}
					formTemplateId={editingForm?.formTemplateId}
					organizationId={user?.organizationId || ""}
				/>
			</div>
		</DashboardLayout>
	);
}
